import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ME_QUERY } from "@/graphql/auth";
import { isValidLanguage, Language as SupportedLanguage } from "@/lib/i18n";
import { useAuthStore } from "@/lib/store/auth-enhanced";

// GraphQL mutation to update user's preferred language
const UPDATE_USER_LANGUAGE_MUTATION = gql`
  mutation UpdateUserLanguage($preferredLanguage: Language!) {
    updateUserLanguage(input: { preferredLanguage: $preferredLanguage }) {
      id
      preferredLanguage
    }
  }
`;

/**
 * Hook for managing user language preferences
 * Integrates with both local i18n state and backend user preferences
 */
export function useLanguagePreference() {
  const { i18n, t } = useTranslation();
  const { user, isAuthenticated, updateUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);

  const [updateUserLanguage, { loading: isUpdating }] = useMutation(UPDATE_USER_LANGUAGE_MUTATION, {
    onCompleted: (data) => {
      if (data?.updateUserLanguage?.preferredLanguage) {
        updateUser({
          ...useAuthStore.getState().user,
          preferredLanguage: data.updateUserLanguage.preferredLanguage,
        } as Parameters<typeof updateUser>[0]);
      }
      toast.success(t("settings:messages.languageChanged"));
    },
    onError: (error) => {
      toast.error(error.message || t("common:errors.general"));
    },
  });

  const [fetchUser, { loading: isFetchingUser }] = useLazyQuery(ME_QUERY, {
    onCompleted: (data) => {
      if (data.me) {
        // Update user data in auth store with fresh data including preferredLanguage
        // Use updateUser to avoid emitting USER_LOGGED_IN which would cause re-renders
        const currentUser = useAuthStore.getState().user;
        const updatedUser = { ...currentUser, ...data.me };
        updateUser(updatedUser);
      }
      // Mark loading as complete after user data is fetched
      setIsLoading(false);
    },
    onError: (error) => {
      console.error("Failed to fetch fresh user data:", error);
      // Even if there's an error, mark loading as complete
      setIsLoading(false);
    },
  });

  // Fetch fresh user data once when user is authenticated
  // AuthGuard guarantees auth is READY before this hook mounts
  // Note: `user` intentionally excluded from deps — we only want to fetch once on auth,
  // not re-fetch every time user data updates (which would cause an infinite loop)
  useEffect(() => {
    if (isAuthenticated) {
      fetchUser();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, fetchUser]);

  // Initialize language from user preference or localStorage
  useEffect(() => {
    if (user?.preferredLanguage && isValidLanguage(user.preferredLanguage.toLowerCase())) {
      const userLang = user.preferredLanguage.toLowerCase() as SupportedLanguage;
      if (i18n.language !== userLang) {
        i18n.changeLanguage(userLang);
      }
      // If user has preference and no fetch is needed, mark as complete
      if (!isFetchingUser) {
        setIsLoading(false);
      }
    } else {
      // Fall back to localStorage
      const savedLanguage = localStorage.getItem("salon-language");
      if (savedLanguage && isValidLanguage(savedLanguage) && i18n.language !== savedLanguage) {
        i18n.changeLanguage(savedLanguage);
      }
      // If no user preference and no fetch is needed, mark as complete
      if (!isFetchingUser) {
        setIsLoading(false);
      }
    }
  }, [user?.preferredLanguage, isFetchingUser, i18n.changeLanguage, i18n.language]);

  // Change language and optionally save to backend
  const changeLanguage = useCallback(
    async (languageCode: SupportedLanguage, saveToBackend = true) => {
      if (!isValidLanguage(languageCode)) {
        console.warn(`Invalid language code: ${languageCode}`);
        return;
      }

      // Update local i18n immediately
      await i18n.changeLanguage(languageCode);
      localStorage.setItem("salon-language", languageCode);

      // Save to backend if user is authenticated and option is enabled
      if (saveToBackend && user) {
        try {
          await updateUserLanguage({
            variables: {
              preferredLanguage: languageCode, // Use lowercase as per GraphQL enum (en, de, vi)
            },
          });
        } catch (error) {
          // Don't throw - the local change has already been applied
          console.error("Failed to save language preference to backend:", error);
        }
      }
    },
    [i18n, user, updateUserLanguage]
  );

  // Get current language
  const currentLanguage = i18n.language as SupportedLanguage;

  // Check if language preference is being saved
  const isSaving = isUpdating;

  return {
    currentLanguage,
    changeLanguage,
    isSaving,
    isLoading,
  };
}

/**
 * Hook for temporary language changes (e.g., for appointment booking)
 * Does not save to backend or localStorage
 */
export function useTemporaryLanguage() {
  const { i18n } = useTranslation();

  const setTemporaryLanguage = useCallback(
    (languageCode: SupportedLanguage) => {
      if (isValidLanguage(languageCode)) {
        i18n.changeLanguage(languageCode);
      }
    },
    [i18n]
  );

  const resetLanguage = useCallback(() => {
    const savedLanguage = localStorage.getItem("salon-language");
    if (savedLanguage && isValidLanguage(savedLanguage)) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  return {
    setTemporaryLanguage,
    resetLanguage,
    currentLanguage: i18n.language as SupportedLanguage,
  };
}
